import type { ReactNode } from 'react'
import { Stack, Text, type IconAmbulance, Box } from '@n26/nxd'
import { useGetLocale } from '@hooks/useGetLocale'
import { Link } from '@components/Link'
import type { LinkProps } from '@components/Link'
import { isLinkExternal } from '@helpers/core/isLinkExternal'

type IconProps = Parameters<typeof IconAmbulance>[0]

type IconWithTextLinkProps = Omit<LinkProps, 'baseUrl'> & {
  text: ReactNode
  addLocaleToInternalUrl?: boolean
  StartIcon?: React.NamedExoticComponent<IconProps>
  EndIcon?: React.NamedExoticComponent<IconProps>
  fontColor?:
    | 'teal'
    | 'petrol'
    | 'wheat'
    | 'rhubarb'
    | 'neutral'
    | 'neutral-alternate'
    | 'neutral-tertiary'
    | 'petrol-alternate'
    | 'rhubarb-alternate'
    | 'teal-alternate'
    | 'wheat-alternate'
}

const IconWithTextLink = ({
  url,
  addLocaleToInternalUrl = true,
  text,
  StartIcon,
  EndIcon,
  fontColor = 'neutral',
  ...rest
}: IconWithTextLinkProps) => {
  const locale = useGetLocale()
  const internalUrl = addLocaleToInternalUrl ? `/${locale}${url}` : url
  const href = isLinkExternal(url) ? url : internalUrl

  // the interpolation of the fontColor variable does not work with tailwind
  const underlineHoverColor = (
    fontColor: IconWithTextLinkProps['fontColor']
  ) => {
    switch (fontColor) {
      case 'neutral':
        return 'hover:border-black'
      case 'neutral-tertiary':
        return 'hover:border-neutral-tertiary'
      case 'neutral-alternate':
        return 'hover:border-neutral-alternate'
      case 'petrol':
        return 'hover:border-petrol'
      case 'petrol-alternate':
        return 'hover:border-petrol-alternate'
      case 'teal':
        return 'hover:border-teal'
      case 'teal-alternate':
        return 'hover:border-teal-alternate'
      case 'wheat':
        return 'hover:border-wheat'
      case 'wheat-alternate':
        return 'hover:border-wheat-alternate'
      case 'rhubarb':
        return 'hover:border-rhubarb'
      case 'rhubarb-alternate':
        return 'hover:border-rhubarb-alternate'
    }
  }

  return locale ? (
    <Link url={href} className='text-16 sm:text-18' {...rest}>
      <Stack space='4' direction='horizontal' as='span' align='center'>
        {StartIcon && <StartIcon color={fontColor} data-testid='start-icon' />}
        <Box
          className={`border-b-2 border-transparent ${underlineHoverColor(
            fontColor
          )} transition-colors duration-200 ease-in-out`}
        >
          {/* We need to keep the NXD component here to support the different colors */}
          <Text variant='standard-bold' color={fontColor}>
            {text}
          </Text>
        </Box>
        {EndIcon && <EndIcon color={fontColor} data-testid='end-icon' />}
      </Stack>
    </Link>
  ) : null
}

export { IconWithTextLink }
